import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { practiceAPI } from '../../api';

const PracticeSession = () => {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [practiceSet, setPracticeSet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});        // { index: selectedOptionLetter }
  const [answerResults, setAnswerResults] = useState({});    // { index: 'correct' | 'wrong' | null }
  const [sessionId, setSessionId] = useState(null);
  const [mode, setMode] = useState('immediate');
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadSet = async () => {
      try {
        const res = await practiceAPI.getSet(setId);
        setPracticeSet(res.data);
        const qs = res.data.questions || [];
        setQuestions(qs);
        const sessionRes = await practiceAPI.startSession(setId);
        setSessionId(sessionRes.data.session_id);
      } catch (err) {
        toast.error('Failed to load practice set');
        navigate('/participate/preparena');
      } finally {
        setLoading(false);
      }
    };
    loadSet();
  }, [setId, navigate]);

  const checkAnswer = (question, selectedLetter) => selectedLetter === question.answer;

  const handleAnswerByIndex = (idx, selectedLetter) => {
    const q = questions[idx];
    const isCorrect = checkAnswer(q, selectedLetter);
    
    // Store answer and result
    setUserAnswers(prev => ({ ...prev, [idx]: selectedLetter }));
    setAnswerResults(prev => ({ ...prev, [idx]: isCorrect ? 'correct' : 'wrong' }));

    if (mode === 'immediate') {
      // Toast feedback
      toast(isCorrect ? '✅ Correct!' : '❌ Wrong!', {
        type: isCorrect ? 'success' : 'error',
        autoClose: 1500,
      });
    }
  };

  const calculateScore = () => {
    let correctCount = 0;
    questions.forEach((q, idx) => {
      const selected = userAnswers[idx];
      if (selected && checkAnswer(q, selected)) correctCount++;
    });
    const total = questions.length;
    const percent = total === 0 ? 0 : (correctCount / total) * 100;
    return { correctCount, total, percent };
  };

  const submitPractice = async () => {
    if (submitting) return;
    setSubmitting(true);
    const { correctCount, total, percent } = calculateScore();
    setShowResults(true);
    try {
      // Use query parameters now
      await practiceAPI.submitSession(sessionId, percent, correctCount);
    } catch (err) {
      console.error('Failed to save session', err);
    }
    setSubmitting(false);
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1) setCurrentIndex(prev => prev + 1);
    else submitPractice();
  };

  const prevQuestion = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const restart = () => {
    navigate(0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (showResults) {
    const { correctCount, total, percent } = calculateScore();
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 text-center">
          <div className="mb-6">
            <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${percent >= 70 ? 'bg-green-100' : 'bg-orange-100'}`}>
              <svg className={`w-12 h-12 ${percent >= 70 ? 'text-green-600' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Practice Completed!</h2>
          <p className="text-gray-600 mb-6">You answered {correctCount} out of {total} correctly.</p>
          <div className="text-5xl font-bold mb-6">{Math.round(percent)}%</div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={restart} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium">Practice Again</button>
            <button onClick={() => navigate('/participate/preparena')} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium">Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <h2 className="text-xl font-bold text-red-600">No questions found</h2>
          <button onClick={() => navigate('/participate/preparena')} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded">Go Back</button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const currentAnswer = userAnswers[currentIndex];
  const currentResult = answerResults[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-wrap justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{practiceSet?.title}</h1>
              <div className="text-sm text-gray-500 mt-1">Question {currentIndex+1} of {questions.length}</div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="mode" checked={mode === 'immediate'} onChange={() => setMode('immediate')} />
                Show answers immediately
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="mode" checked={mode === 'final'} onChange={() => setMode('final')} />
                Show at the end
              </label>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${((currentIndex+1)/questions.length)*100}%` }}></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <div className="mb-6 prose max-w-none">
            {currentQ.question}
          </div>

          {/* Render options with visual feedback */}
          <div className="space-y-3">
            {currentQ.options.map((opt) => {
              const isSelected = currentAnswer === opt.option;
              let borderColor = 'border-gray-200';
              let bgColor = '';
              if (isSelected && currentResult) {
                borderColor = currentResult === 'correct' ? 'border-green-500' : 'border-red-500';
                bgColor = currentResult === 'correct' ? 'bg-green-50' : 'bg-red-50';
              } else if (isSelected) {
                borderColor = 'border-blue-500';
                bgColor = 'bg-blue-50';
              }
              return (
                <label
                  key={opt.option}
                  className={`flex items-start p-4 rounded-xl border-2 cursor-pointer ${borderColor} ${bgColor}`}
                >
                  <input
                    type="radio"
                    name="question"
                    value={opt.option}
                    checked={isSelected}
                    onChange={() => handleAnswerByIndex(currentIndex, opt.option)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <span className="font-semibold mr-2">{opt.option}.</span>
                    <span dangerouslySetInnerHTML={{ __html: opt.content }} />
                  </div>
                  {isSelected && currentResult && (
                    <span className="ml-2 text-lg">
                      {currentResult === 'correct' ? '✅' : '❌'}
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={prevQuestion}
              disabled={currentIndex === 0}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={nextQuestion}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg"
            >
              {currentIndex === questions.length-1 ? 'Submit' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeSession;